import React, { useCallback, useEffect } from 'react';

import { debounce } from '@mui/material';

import {
  AdminLegendVariant,
  searchUsers,
  ConfirmationTypes,
  adminResetValue,
  HelpTypes,
  adminActions,
  resetStatus,
  successOrSelector,
  setLongTermContractAttributes,
} from '@pharmaplan/common';
import { DateTime } from 'luxon';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { setDialog } from '../../../reducers/dialogReducer';

import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../../localization';
import { resetDrawer } from '../../../actions/drawerActions';
import useAdminDashboard from '../useAdminDashboard';
import useExpressBookingTypes from './useExpressBookingTypes';
import useExpressBookingFormik from './useExpressBookingFormik';
import { resetSecondaryRoute } from '../../../actions/navbarActions';
import { toJSDate } from '../../../components/Dashboard/MainCalendar/helpers';
import useUpdateContract from '../../LongTermContract/useUpdateContract';

const { booked } = AdminLegendVariant;

const useExpressBooking = (
  type: Exclude<AdminLegendVariant, AdminLegendVariant.total>,
  id?: string,
  noActionsScreen?: boolean,
) => {
  const dispatch = useAppDispatch();

  const { dashBasedRequest } = useAdminDashboard();
  const { detailsApiMap } = useExpressBookingTypes();
  const { formatDisplayDate } = useUpdateContract();
  const { getFormikInitialValues, bookWorkshift, bookLongTerm } = useExpressBookingFormik({
    type,
    id,
  });

  const isBooked = type === booked;

  const success = useAppSelector((state) =>
    successOrSelector(
      [adminActions.expressBooking, adminActions.longTermExpressBooking],
      state,
    ));

  const confirmAction = (successAction: () => void) => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.expressBooking}
            showCloseButton
            confirmAction={successAction}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const getInput = (input: string, replacementType: HelpTypes) => {
    if (input) {
      dispatch(
        searchUsers({
          userTypes: [replacementType],
          query: input,
        }),
      );
    }
  };

  const getPharmacyInput = (input: string) => {
    if (input) {
      dispatch(
        searchUsers({
          userTypes: [HelpTypes.pharmacy],
          query: input,
          showAppointedPharmacies: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(detailsApiMap[type].api({ id }));
    }

    return () => {
      dispatch(adminResetValue(['searchedPharmacists', 'searchedPharmacies']));
      dispatch(adminResetValue([detailsApiMap[type].state]));
      dispatch(resetSecondaryRoute());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successMsg = id && isBooked
    ? strings.bookingEditedSuccessfully
    : strings.bookingCreatedSuccessfully;

  const successRequest = () => {
    if (!noActionsScreen) {
      dashBasedRequest();
    }
  };

  useEffect(() => {
    if (success) {
      successRequest();
      showSuccess(dispatch, successMsg);
      dispatch(
        resetStatus([
          adminActions.expressBooking,
          adminActions.longTermExpressBooking,
        ]),
      );
      dispatch(resetDrawer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    const { longTermShiftContract, allShifts } = detailsApiMap[type]?.details ?? {};
    if (longTermShiftContract && !!id) {
      const { startDate, endDate } = longTermShiftContract ?? {};
      const start = DateTime.fromISO(startDate, { setZone: true });
      const end = DateTime.fromISO(endDate, { setZone: true });
      const sTime = toJSDate(startDate);
      const eTime = toJSDate(endDate);

      dispatch(
        setLongTermContractAttributes({
          defaultStartTime: sTime,
          defaultEndTime: eTime,
          startDate: start,
          endDate: end,
          shiftContractAttributes: allShifts ?? [],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsApiMap[type]?.details?.allShifts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInput = useCallback(debounce(getInput, 300), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPharmacyInput = useCallback(
    debounce(getPharmacyInput, 300),
    [],
  );

  return {
    confirmAction,
    detailsApiMap,
    debouncedInput,
    getFormikInitialValues,
    debouncedPharmacyInput,
    bookWorkshift,
    bookLongTerm,
    formatDisplayDate,
  };
};

export default useExpressBooking;
